/**
 * Credit accounting vocabulary.
 *
 * The ledger is append-only and the balance is the sum of deltas — there is no
 * mutable balance column anywhere in the system, so there is nothing to drift.
 * Redis caches the sum; Postgres remains the source of truth.
 */
import { z } from 'zod';
export const creditReasonSchema = z.enum([
    'verification_grant',
    'purchase',
    'generation_reserve',
    'generation_settle',
    'generation_refund',
    'manual_adjustment',
    'payment_refund',
    'chargeback',
]);
/**
 * Sign constraints, enforced in the ledger service.
 *
 * `generation_settle` is in neither list: it returns the unspent part of a
 * reservation, which is zero when the generation cost exactly what was held.
 */
export const DEBIT_REASONS = [
    'generation_reserve',
    'chargeback',
    'payment_refund',
];
export const CREDIT_REASONS = [
    'verification_grant',
    'purchase',
    'generation_refund',
];
/** Human-facing labels for the ledger table. */
/**
 * What a history row says to a customer.
 *
 * These are rendered, so they are a customer-facing surface even though they
 * live beside internal enum names. Two of them still carried the old wording
 * after the rest of the vocabulary had changed, and reached the shipped bundle:
 * the linter scanned the SPA, and these come from the shared package. That is
 * the gap any scan defined by a directory list will always have.
 *
 * The old strings are deliberately not quoted here. A comment naming them ends
 * up in the bundle too, where anyone grepping it finds the forbidden phrase and
 * cannot tell a note from a label.
 */
export const CREDIT_REASON_LABELS = {
    verification_grant: 'Welcome generations',
    purchase: 'Service pack',
    generation_reserve: 'Generation started',
    generation_settle: 'Generation settled',
    generation_refund: 'Generation refunded',
    manual_adjustment: 'Manual adjustment',
    payment_refund: 'Payment refunded',
    chargeback: 'Chargeback',
};
/**
 * What a generation costs, charged as reserve-then-settle. Repairs are free: a
 * repair only happens because our validator rejected our own model's output, and
 * charging the user for that is indefensible.
 */
export const GENERATION_COST_CREDITS = {
    create: 5,
    edit: 2,
    repair: 0,
};
/**
 * Granted on *email verification*, not on signup. Tying it to a verified address
 * makes the trial cost one working inbox per grant rather than one form
 * submission, which is the cheapest abuse control available here.
 *
 * Sized to exactly one create generation (see GENERATION_COST_CREDITS) with
 * enough left for a few edits.
 */
export const VERIFICATION_GRANT_CREDITS = 20;
/** Redis key holding the cached balance. Invalidated on every ledger write. */
export const balanceCacheKey = (userId) => `credits:balance:${userId}`;
/**
 * Idempotency keys are constructed, never random, so that a webhook replay or a
 * job redelivery collides with the original write instead of duplicating it.
 */
export const idempotencyKey = (scope, ref, step) => `${scope}:${ref}:${step}`;
export const creditLedgerEntrySchema = z.object({
    userId: z.uuid(),
    delta: z.int(),
    reason: creditReasonSchema,
    refType: z.enum(['generation', 'payment', 'user']).nullable(),
    refId: z.string().nullable(),
    idempotencyKey: z.string().min(8).max(128),
});
/** One row of the dashboard's ledger table. */
export const ledgerEntrySummarySchema = z.object({
    id: z.uuid(),
    delta: z.number().int(),
    balanceAfter: z.number().int(),
    reason: creditReasonSchema,
    refType: z.string().nullable(),
    refId: z.string().nullable(),
    createdAt: z.string(),
});
export const balanceResponseSchema = z.object({
    balance: z.number().int(),
    /** True when the number came from Redis rather than a fresh SUM. */
    cached: z.boolean(),
});
export const ledgerPageSchema = z.object({
    entries: z.array(ledgerEntrySummarySchema),
    nextCursor: z.string().nullable(),
});
/**
 * What the dashboard renders.
 *
 * A count and a date. There is deliberately no monetary field — a response that
 * cannot carry a currency figure is a view that cannot accidentally render one.
 */
export const remainingResponseSchema = z.object({
    generations: z.number().int().nonnegative(),
    nextExpiry: z.string().nullable(),
});
//# sourceMappingURL=credits.js.map